import { useState, useEffect, useRef } from "react";
import { Box, Button, Input, Tag, Text } from "@chakra-ui/react";
import { useChatBot } from "../api";

type IProps = {
  aiName: string;
  prompts: string[];
  color?: "red" | "purple" | "greens";
  system: string;
  topPanel?: boolean;
};
const GeneralAndNarrowAi = ({
  aiName,
  prompts,
  system,
  color,
  topPanel = true,
}: IProps) => {
  const chatbot = useChatBot();
  const [error, setError] = useState<null | string>(null);
  const [messages, setMessages] = useState([
    { message: `Hi, this is ${aiName}`, type: "system" },
  ]);

  const [input, setInput] = useState("");
  const [cooldown, setCooldown] = useState(false);
  const [requestCount, setRequestCount] = useState(0);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (requestCount > 0) {
      const resetCount = setTimeout(() => setRequestCount(0), 60000); // Reset after 1 minute
      return () => clearTimeout(resetCount);
    }
  }, [requestCount]);

  const getResponseFromAiApi = async (userMessage: string) => {
    chatbot.mutate(
      { system, prompt: userMessage },
      {
        onSuccess: (res) => {
          setMessages((prev) => [
            ...prev,
            { message: res.response, type: "system" },
          ]);
          setError(null);
        },
        onError: () => {
          setError("Something went wrong. please try again");
        },
      }
    );
  };

  const handleAddPrompt = async () => {
    if (!input.trim() || cooldown || requestCount >= 7) return;

    setCooldown(true);
    setRequestCount((prev) => prev + 1);
    setTimeout(() => setCooldown(false), 3000); // 3-second cooldown

    const newUserMessage = { message: `${input}`, type: "user" };
    setMessages((prev) => [...prev, newUserMessage]);
    setInput("");

    getResponseFromAiApi(input);
  };

  return (
    <Box maxW="33rem" mx="auto" px={".5rem"}>
      <Box width="100%">
        <Box
          display={topPanel ? "block" : "none"}
          mb={"-.7rem"}
          borderTopRadius={"1rem"}
          bg={`${color}.800`}
          p={"1rem"}
        >
          {aiName}
        </Box>
        <Box
          bg="#1E1E1E"
          height="13rem"
          overflowY="auto"
          width="100%"
          p="1rem"
          borderTopRadius={topPanel ? 0 : "1rem"}
          ref={messagesContainerRef}
          overflow={"hidden"}
        >
          {messages.map((m, index) => (
            <Box
              key={index}
              p=".5rem 1rem"
              width="70%"
              borderRadius="1rem"
              bg={m.type === "user" ? "neutral.800" : "neutral.700"}
              ml={m.type === "user" ? "none" : "auto"}
              my=".5rem"
            >
              {m.message}
            </Box>
          ))}
          <div ref={messagesEndRef} />
        </Box>
        <Box
          p={"1rem"}
          bg="#101010"
          border={"1px solid"}
          borderColor={"neutral.900"}
          borderBottomRadius={"1rem"}
          borderTop={"none"}
        >
          <Box display="flex" gap="0.5rem">
            <Input
              placeholder="Type your message..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              isDisabled={cooldown || requestCount >= 5}
            />
            <Button
              variant="secondary"
              onClick={handleAddPrompt}
              isDisabled={cooldown || requestCount >= 5}
              isLoading={chatbot.isPending}
            >
              {cooldown ? "Wait..." : "Send"}
            </Button>
          </Box>

          {requestCount >= 5 && (
            <Text color="red.400" mt="0.5rem">
              ⚠️ Rate limit reached! Try again in a minute.
            </Text>
          )}
          {error && (
            <Text color="red.400" mt="0.5rem">
              ⚠️ {error}
            </Text>
          )}
        </Box>
      </Box>
      <Box mt="1rem" display="flex" flexWrap="wrap" gap="1rem">
        {prompts.map((p) => (
          <Tag
            variant="outline"
            cursor="pointer"
            size="lg"
            borderRadius={"1rem"}
            onClick={() => setInput(p)}
            borderColor={"neutral.900"}
          >
            {p}
          </Tag>
        ))}
      </Box>
    </Box>
  );
};

export default GeneralAndNarrowAi;
